/**
 * Plan of Record — the address and its share tokens (migration 721).
 *
 * The record lives at one permanent address:
 *   /record/:porId                 the standing record (latest authoritative version)
 *   /record/:porId/v/:n            one version
 *   /record/:porId/c/:commitmentId one commitment (at the latest version)
 *   /record/:porId/v/:n/c/:cid     one commitment at one version
 *
 * Workspace members read it under RLS. Anyone else who is signed in reads it
 * with a scoped, revocable share token appended as `?t=…` — the same address,
 * a key in the query string. Minting and revoking are RPCs with the
 * ratification authority bar and a server-enforced module opt-in; the token
 * read is one SECURITY DEFINER function that strips personal identifiers.
 *
 * Migration 724 (owner ruling 2026-09-19): anonymous EXECUTE on the token read
 * is revoked — a share link resolves only for a signed-in caller. A reader with
 * no session gets `sign_in_required`, never `unavailable`.
 *
 * Spec: docs/9-exploration/SPEC-plan-of-record-redesign.md §4 Move 2 / Move 7.
 */

import { getSupabaseClient } from './supabaseClient';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { AssembledPor, PorCommitment, PorSignalEvent } from '../types/plan-of-record';

export type ShareScope = 'record' | 'version' | 'commitment';

export interface PorShareToken {
  id: string;
  workspaceId: string;
  porId: string;
  scope: ShareScope;
  version: number | null;
  commitmentId: string | null;
  token: string;
  label: string | null;
  createdBy: string | null;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  viewCount: number;
  lastViewedAt: string | null;
}

export const SHARE_SCOPE_LABELS: Readonly<Record<ShareScope, string>> = {
  record: 'Whole record — every version, every commitment',
  version: 'One version only',
  commitment: 'One commitment only',
};

// ── Addresses ─────────────────────────────────────────────────────────────────

export interface RecordAddressParts {
  version?: number | null;
  commitmentId?: string | null;
}

/** The canonical, permanent path of a record, version, or commitment. */
export function recordAddress(porId: string, parts: RecordAddressParts = {}): string {
  let path = `/record/${porId}`;
  if (parts.version != null) path += `/v/${parts.version}`;
  if (parts.commitmentId) path += `/c/${parts.commitmentId}`;
  return path;
}

/** Append a share token to an address. */
export function withShareToken(path: string, token: string | null | undefined): string {
  if (!token) return path;
  return `${path}${path.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}`;
}

/** Absolute URL for copying and sending. */
export function absoluteUrl(path: string): string {
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

/** The address a token points at by default (its narrowest scope). */
export function tokenAddress(t: Pick<PorShareToken, 'porId' | 'scope' | 'version' | 'commitmentId' | 'token'>): string {
  const parts: RecordAddressParts =
    t.scope === 'record' ? {} : t.scope === 'version' ? { version: t.version } : { version: t.version, commitmentId: t.commitmentId };
  return withShareToken(recordAddress(t.porId, parts), t.token);
}

// ── Member side: list, mint, revoke ───────────────────────────────────────────

function toToken(row: Record<string, unknown>): PorShareToken {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    porId: row.por_id as string,
    scope: row.scope as ShareScope,
    version: (row.version as number | null) ?? null,
    commitmentId: (row.commitment_id as string | null) ?? null,
    token: row.token as string,
    label: (row.label as string | null) ?? null,
    createdBy: (row.created_by as string | null) ?? null,
    createdAt: row.created_at as string,
    expiresAt: (row.expires_at as string | null) ?? null,
    revokedAt: (row.revoked_at as string | null) ?? null,
    viewCount: (row.view_count as number) ?? 0,
    lastViewedAt: (row.last_viewed_at as string | null) ?? null,
  };
}

export async function listShareTokens(porId: string): Promise<{ data: PorShareToken[]; error: string | null }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('por_share_tokens')
    .select('*')
    .eq('por_id', porId)
    .order('created_at', { ascending: false });
  if (error) return { data: [], error: error.message };
  return { data: (data ?? []).map((r) => toToken(r as unknown as Record<string, unknown>)), error: null };
}

export interface CreateShareTokenInput {
  porId: string;
  scope: ShareScope;
  version?: number | null;
  commitmentId?: string | null;
  label?: string | null;
  /** ISO timestamp; omit for no expiry. */
  expiresAt?: string | null;
}

export async function createShareToken(
  input: CreateShareTokenInput,
): Promise<{ data: { id: string; token: string; address: string } | null; error: string | null }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('create_por_share_token', {
    p_por_id: input.porId,
    p_scope: input.scope,
    p_version: input.version ?? null,
    p_commitment_id: input.commitmentId ?? null,
    p_label: input.label ?? null,
    p_expires_at: input.expiresAt ?? null,
  });
  if (error) return { data: null, error: error.message };
  const res = data as { success: boolean; error?: string; id?: string; token?: string; version?: number | null; commitment_id?: string | null; scope?: ShareScope };
  if (!res?.success || !res.token || !res.id) return { data: null, error: res?.error ?? 'Could not create share link.' };
  const address = tokenAddress({
    porId: input.porId,
    scope: res.scope ?? input.scope,
    version: res.version ?? null,
    commitmentId: res.commitment_id ?? null,
    token: res.token,
  });
  return { data: { id: res.id, token: res.token, address }, error: null };
}

export async function revokeShareToken(tokenId: string, rationale?: string): Promise<{ error: string | null }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('revoke_por_share_token', { p_token_id: tokenId, p_rationale: rationale ?? null });
  if (error) return { error: error.message };
  const res = data as { success: boolean; error?: string };
  return { error: res?.success ? null : (res?.error ?? 'Could not revoke share link.') };
}

// ── Recipient side: read by token (signed-in callers only — migration 724) ────

export type ShareReadError =
  | 'invalid_token'
  | 'not_found'
  | 'revoked'
  | 'expired'
  | 'out_of_scope'
  | 'no_record'
  | 'sign_in_required'
  | 'unavailable';

/** Postgres "insufficient privilege" / PostgREST auth refusals — the caller may not execute the read. */
function isPermissionError(error: { code?: string | null; message?: string | null; status?: number | null }): boolean {
  if (error.code === '42501' || error.code === 'PGRST301' || error.code === 'PGRST302') return true;
  if (error.status === 401 || error.status === 403) return true;
  return /permission denied/i.test(error.message ?? '');
}

export interface SharedRecordRead {
  scope: ShareScope;
  record: { id: string; title: string; workspaceName: string | null };
  snapshot: {
    version: number;
    authorizedAt: string;
    ratificationAuthority: string;
    rationale: string;
    superseded: boolean;
    hasCycleReport: boolean;
    hasPlanVersion: boolean;
  };
  versions: Array<{ version: number; authorizedAt: string; ratificationAuthority: string; superseded: boolean }>;
  /** Null for a commitment-scoped token. Personal identifiers are stripped server-side. */
  payload: AssembledPor | null;
  commitment: PorCommitment | null;
  linkedSignalEvents: PorSignalEvent[];
}

export async function readRecordByShareToken(
  token: string,
  parts: RecordAddressParts = {},
): Promise<{ data: SharedRecordRead | null; error: ShareReadError | null }> {
  const supabase = getSupabaseClient();
  // Migration 724: the read is not executable anonymously. Check the session
  // first so a signed-out reader is asked to sign in instead of seeing a fault.
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) return { data: null, error: 'sign_in_required' };
  const { data, error } = await supabase.rpc('read_por_by_share_token', {
    p_token: token,
    p_version: parts.version ?? null,
    p_commitment_id: parts.commitmentId ?? null,
  });
  if (error) return { data: null, error: isPermissionError(error) ? 'sign_in_required' : 'unavailable' };
  const res = data as Record<string, unknown> | null;
  if (!res || res.success !== true) {
    const code = (res?.error as string | undefined) ?? 'unavailable';
    const known: ShareReadError[] = ['invalid_token', 'not_found', 'revoked', 'expired', 'out_of_scope', 'no_record'];
    return { data: null, error: (known as string[]).includes(code) ? (code as ShareReadError) : 'unavailable' };
  }
  const rec = res.record as Record<string, unknown>;
  const snap = res.snapshot as Record<string, unknown>;
  return {
    data: {
      scope: res.scope as ShareScope,
      record: {
        id: rec.id as string,
        title: rec.title as string,
        workspaceName: (rec.workspace_name as string | null) ?? null,
      },
      snapshot: {
        version: snap.version as number,
        authorizedAt: snap.authorized_at as string,
        ratificationAuthority: snap.ratification_authority as string,
        rationale: snap.rationale as string,
        superseded: Boolean(snap.superseded),
        hasCycleReport: Boolean(snap.has_cycle_report),
        hasPlanVersion: Boolean(snap.has_plan_version),
      },
      versions: ((res.versions as Array<Record<string, unknown>>) ?? []).map((v) => ({
        version: v.version as number,
        authorizedAt: v.authorized_at as string,
        ratificationAuthority: v.ratification_authority as string,
        superseded: Boolean(v.superseded),
      })),
      payload: (res.payload as AssembledPor | null) ?? null,
      commitment: (res.commitment as PorCommitment | null) ?? null,
      linkedSignalEvents: ((res.linked_signal_events as PorSignalEvent[]) ?? []),
    },
    error: null,
  };
}
